import { demoModeAtom, tokenExpiresAtom, userAtom } from '@/global-store';
import { createUnauthenticatedError } from '@/http/authErrors';
import { api, demoTokenExpires, demoUser } from '@/http/constants';
import fetchWrapper from '@/http/fetchWrapper';
import type { UserData } from '@eye-able/types';
import { useQuery } from '@tanstack/react-query';
import { useAtom } from 'jotai';

const offlineMode = import.meta.env.VITE_OFFLINE_MODE === 'true';

export function useFetchWhoami() {
  const [user, setUser] = useAtom(userAtom);
  const [tokenExpires, setTokenExpires] = useAtom(tokenExpiresAtom);
  const [demoMode] = useAtom(demoModeAtom);

  return useQuery({
    queryKey: [api.whoami, tokenExpires, demoMode],
    initialData: user,
    queryFn: async () => {
      if (demoMode || offlineMode) {
        setTokenExpires(demoTokenExpires);
        setUser(demoUser);
        return demoUser;
      }

      // These local checks are authoritative session failures, just like a
      // 401 from the API, and should still send the user to login.
      if (!tokenExpires) {
        throw createUnauthenticatedError('No tokenExpires');
      }

      if (
        Number(tokenExpires) <
        Number((new Date().getTime() / 1000).toFixed(0)) - 14400
      ) {
        throw createUnauthenticatedError('Token expired');
      }

      const data = await fetchWrapper.get<UserData>(api.whoami);

      setUser(data);

      return data;
    },
  });
}
